import { lazy } from 'react';

const RELOAD_KEY = 'chunk-reload-at';
const RELOAD_WINDOW_MS = 10000;

/**
 * `React.lazy` that survives a deploy.
 *
 * Every build gives its chunks new hashed file names and removes the old ones.
 * A tab opened before the deploy is still running the old build, so the next
 * lazy import asks for a file that no longer exists and the app falls over
 * until the user hard-refreshes. Reloading once fetches the new build.
 *
 * The timestamp guard stops a reload loop when the chunk is missing for some
 * other reason (offline, a broken deploy): within the window it lets the error
 * through instead of reloading again.
 */
export const lazyWithReload = (factory) =>
  lazy(async () => {
    try {
      const module = await factory();
      try {
        window.sessionStorage.removeItem(RELOAD_KEY);
      } catch {
        // Storage unavailable; nothing to clear.
      }
      return module;
    } catch (err) {
      let lastReload = 0;
      try {
        lastReload = Number(window.sessionStorage.getItem(RELOAD_KEY)) || 0;
      } catch {
        // Storage unavailable; fall through and reload once.
      }

      if (Date.now() - lastReload > RELOAD_WINDOW_MS) {
        try {
          window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        } catch {
          // Storage unavailable; the reload still helps.
        }
        window.location.reload();
        // Keep Suspense showing its fallback while the page reloads.
        return new Promise(() => {});
      }

      throw err;
    }
  });
